import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import "@/App.css";

const ClinicalAreas = () => {
  const navigate = useNavigate();
  const [selectedArea, setSelectedArea] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const clinicalAreas = [
    {
      id: "weight-management",
      title: "Weight Management",
      description: "GLP-1 therapies and structured prescribing pathways",
      modalContent: {
        intro: "Pharmacist-led prescribing support for structured weight management services, aligned to your clinical protocols.",
        includes: [
          "GLP-1 therapies and related treatments",
          "Eligibility and safety checks",
          "Dose titration and ongoing review",
          "Clinical escalation pathways"
        ],
        footer: "Delivered within your SOPs and governance framework."
      }
    },
    {
      id: "mens-health",
      title: "Men's Health",
      description: "Testosterone, erectile dysfunction, and related conditions",
      modalContent: {
        intro: "Prescribing support across common men's health pathways, integrated into your clinical workflow.",
        includes: [
          "Erectile dysfunction treatments",
          "Testosterone therapy (where appropriate)",
          "Hormonal health pathways",
          "Monitoring and review"
        ],
        footer: "Aligned to your SOPs, systems, and governance."
      }
    },
    {
      id: "womens-health",
      title: "Women's Health",
      description: "Contraception, HRT, and hormonal health pathways",
      modalContent: {
        intro: "Clinical prescribing support for women's health services within defined protocols.",
        includes: [
          "Contraception pathways",
          "Hormone replacement therapy (HRT)",
          "Hormonal health treatments",
          "Safety checks and review"
        ],
        footer: "Integrated within your clinical pathways and governance model."
      }
    },
    {
      id: "pain-management",
      title: "Pain Management",
      description: "Structured prescribing for pain-related conditions",
      modalContent: {
        intro: "Structured prescribing support for pain-related conditions within defined clinical protocols.",
        includes: [
          "Acute and chronic pain pathways",
          "Protocol-driven prescribing",
          "Safety monitoring",
          "Escalation routes"
        ],
        footer: "Delivered within your clinical and regulatory framework."
      }
    },
    {
      id: "infection",
      title: "Infection",
      description: "UTIs, STIs, and other acute infections",
      modalContent: {
        intro: "Prescribing capacity for infection-related pathways, supporting timely and safe treatment.",
        includes: [
          "UTIs",
          "STIs",
          "Acute infections",
          "Clinical triage and escalation"
        ],
        footer: "Aligned to your SOPs and safety protocols."
      }
    },
    {
      id: "hair-loss",
      title: "Hair Loss",
      description: "Finasteride, minoxidil, and related treatments",
      modalContent: {
        intro: "Support for hair loss treatment pathways through structured prescribing.",
        includes: [
          "Finasteride",
          "Minoxidil",
          "Eligibility checks",
          "Ongoing review"
        ],
        footer: "Integrated into your clinical workflow."
      }
    },
    {
      id: "mental-health",
      title: "Mental Health",
      description: "Prescribing support within approved clinical frameworks",
      modalContent: {
        intro: "Prescribing support for mental health services within defined governance frameworks.",
        includes: [
          "Protocol-driven prescribing",
          "Clinical assessment processes",
          "Escalation pathways",
          "Monitoring and review"
        ],
        footer: "Delivered within approved clinical governance structures."
      }
    }
  ];

  const scrollToContact = () => {
    setMobileMenuOpen(false);
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const handleMobileNavClick = (action) => {
    setMobileMenuOpen(false);
    if (typeof action === 'function') {
      action();
    }
  };

  const openModal = (area) => {
    setSelectedArea(area);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedArea(null);
    document.body.style.overflow = 'unset';
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      closeModal();
    }
  };

  return (
    <div className="clinical-page" data-testid="clinical-areas-page">
      {/* Navigation */}
      <nav className="nav" data-testid="navigation">
        <div className="nav-container">
          <div className="nav-logo" data-testid="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <span className="logo-text">Precision Prescribing</span>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          {/* Desktop Navigation */}
          <div className="nav-links">
            <button onClick={() => navigate('/clinical-areas')} className="nav-link nav-link-active" data-testid="nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => navigate('/dispensing')} className="nav-link" data-testid="nav-dispensing">Dispensing</button>
            <button onClick={() => navigate('/#how-it-works')} className="nav-link" data-testid="nav-how-it-works">How It Works</button>
            <button onClick={() => navigate('/#about')} className="nav-link" data-testid="nav-about">About</button>
            <button onClick={scrollToContact} className="nav-link nav-cta" data-testid="nav-contact-cta">Request a Discussion</button>
          </div>
          
          {/* Mobile Navigation */}
          <div className={`mobile-nav ${mobileMenuOpen ? 'mobile-nav-open' : ''}`} data-testid="mobile-nav">
            <button onClick={() => handleMobileNavClick(() => navigate('/clinical-areas'))} className="mobile-nav-link mobile-nav-link-active" data-testid="mobile-nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => handleMobileNavClick(() => navigate('/dispensing'))} className="mobile-nav-link" data-testid="mobile-nav-dispensing">Dispensing</button>
            <button onClick={() => handleMobileNavClick(() => navigate('/#how-it-works'))} className="mobile-nav-link" data-testid="mobile-nav-how-it-works">How It Works</button>
            <button onClick={() => handleMobileNavClick(() => navigate('/#about'))} className="mobile-nav-link" data-testid="mobile-nav-about">About</button>
            <button onClick={() => handleMobileNavClick(scrollToContact)} className="mobile-nav-link mobile-nav-cta" data-testid="mobile-nav-contact-cta">Request a Discussion</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="clinical-page-hero" data-testid="clinical-page-hero">
        <div className="section-container">
          <h1 className="clinical-page-title" data-testid="clinical-page-title">Clinical Areas We Support</h1>
          <p className="clinical-page-subtitle" data-testid="clinical-page-subtitle">
            Pharmacist-led prescribing across a range of protocol-driven clinical services, integrated into your clinical workflow.
          </p>
          <div className="clinical-page-hero-cta">
            <button onClick={scrollToContact} className="btn btn-primary" data-testid="clinical-hero-cta-btn">
              Discuss Your Requirements
            </button>
          </div>
        </div>
      </section>

      {/* Clinical Areas List */}
      <section className="clinical-page-content" data-testid="clinical-page-content">
        <div className="section-container">
          <div className="clinical-page-grid" data-testid="clinical-page-grid">
            {clinicalAreas.map((area, index) => (
              <div 
                key={index} 
                className="clinical-page-card clinical-page-card-clickable" 
                data-testid={`clinical-area-${index}`}
                onClick={() => openModal(area)}
              >
                <h3>{area.title}</h3>
                <p>{area.description}</p>
                <span className="card-learn-more">Learn more</span>
              </div>
            ))}
          </div>

          <p className="clinical-pathways-note" data-testid="clinical-pathways-note">
            Additional clinical pathways can be supported based on your SOPs and governance framework.
          </p>
        </div>
      </section>

      {/* How We Integrate Section */}
      <section className="how-integrate-section" data-testid="how-integrate-section">
        <div className="section-container">
          <h2 className="how-integrate-title" data-testid="how-integrate-title">How We Integrate</h2>
          <div className="how-integrate-grid" data-testid="how-integrate-grid">
            <div className="how-integrate-item" data-testid="how-integrate-1">
              <span className="how-integrate-label">Governance-first approach</span>
              <p>Aligned to your SOPs and governance framework</p>
            </div>
            <div className="how-integrate-item" data-testid="how-integrate-2">
              <span className="how-integrate-label">Seamless integration</span>
              <p>Integrated into your systems and clinical workflow</p>
            </div>
            <div className="how-integrate-item" data-testid="how-integrate-3">
              <span className="how-integrate-label">Flexible scaling</span>
              <p>Scalable prescribing capacity as demand grows</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="clinical-page-cta-section" data-testid="clinical-page-cta-section">
        <div className="section-container">
          <div className="clinical-page-cta" data-testid="clinical-page-cta">
            <h3>Looking for a specific clinical pathway?</h3>
            <p>We support a wide range of prescribing services tailored to your clinical model and operational needs.</p>
            <button onClick={scrollToContact} className="btn btn-primary btn-lg" data-testid="clinical-page-cta-btn">
              Discuss Your Requirements
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-container">
          <div className="footer-brand" data-testid="footer-brand">
            <span className="logo-text">Precision Prescribing</span>
            <p className="footer-tagline">Pharmacist-led prescribing capacity for regulated healthcare providers.</p>
          </div>
          <div className="footer-info" data-testid="footer-info">
            <p className="footer-legal-line">Precision Prescribing is a trading name of PHARMKG LTD.</p>
            <p>Company number: 14702569</p>
            <p>ICO registration: ZC107443</p>
            <p>Registered office: 261 High Street, Waltham Cross, EN8 7BE</p>
            <p>Email: <a href="mailto:hello@precisionpx.com">hello@precisionpx.com</a></p>
            <nav className="footer-links" data-testid="footer-links" aria-label="Legal">
              <a href="/privacy-policy" className="footer-link" data-testid="footer-privacy-link">Privacy Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/cookie-policy" className="footer-link" data-testid="footer-cookie-link">Cookie Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/terms-of-use" className="footer-link" data-testid="footer-terms-link">Website Terms of Use</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <button
                onClick={() => navigate("/", { state: { scrollTo: "contact" } })}
                className="footer-link"
                data-testid="footer-contact-link"
              >
                Contact
              </button>
            </nav>
          </div>
        </div>
      </footer>

      {/* Modal */}
      {selectedArea && (
        <div className="modal-overlay" onClick={handleOverlayClick} data-testid="modal-overlay">
          <div className="modal-content" data-testid="modal-content">
            <button className="modal-close" onClick={closeModal} data-testid="modal-close">
              &times;
            </button>
            <h2 className="modal-title" data-testid="modal-title">{selectedArea.title}</h2>
            <p className="modal-intro" data-testid="modal-intro">{selectedArea.modalContent.intro}</p>
            
            <div className="modal-includes" data-testid="modal-includes">
              <p className="modal-includes-label">Includes:</p>
              <ul>
                {selectedArea.modalContent.includes.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            
            <p className="modal-footer" data-testid="modal-footer">{selectedArea.modalContent.footer}</p>
            
            <button onClick={scrollToContact} className="btn btn-primary modal-cta" data-testid="modal-cta-btn">
              Request a Discussion
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicalAreas;
